import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMapPin, FiSearch, FiCheckCircle, FiAlertTriangle, FiCrosshair, FiArrowRight, FiActivity } from 'react-icons/fi';
import { toast } from 'react-toastify';
import satelliteApi from '../services/satelliteApi';
import fmdApi from '../services/fmdApi';
import './ReportLivestock.css';

// Leaflet default marker icon fix (same as SatelliteMap.js)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const RISK_COLORS = { Low: '#38a169', Moderate: '#dd6b20', High: '#e53e3e', Severe: '#8b0000' };

const SPECIES = [
  { key: 'cattle', label: 'Cattle', emoji: '🐄' },
  { key: 'sheep', label: 'Sheep', emoji: '🐑' },
  { key: 'goat', label: 'Goats', emoji: '🐐' },
  { key: 'buffalo', label: 'Buffalo', emoji: '🐃' },
  { key: 'pig', label: 'Pigs', emoji: '🐖' },
];

const SYMPTOMS = [
  { key: 'salivation', label: 'Excess drooling / salivation', emoji: '💧' },
  { key: 'mouth_lesions', label: 'Blisters / sores in mouth', emoji: '👄' },
  { key: 'blisters_feet', label: 'Blisters on feet', emoji: '🦶' },
  { key: 'lameness', label: 'Limping / lameness', emoji: '🦵' },
  { key: 'fever', label: 'Fever', emoji: '🌡️' },
  { key: 'drooling', label: 'Drooling', emoji: '🤤' },
  { key: 'reduced_appetite', label: 'Not eating', emoji: '🚫' },
  { key: 'nasal_discharge', label: 'Runny nose', emoji: '👃' },
  { key: 'weakness', label: 'Weakness', emoji: '😞' },
  { key: 'sudden_death', label: 'Sudden death', emoji: '⚠️' },
];

const DEFAULT_CENTER = [-29.0, 25.0]; // South Africa

// Click-to-set + recenter helper
function LocationPicker({ position, onChange }) {
  useMapEvents({
    click(e) {
      onChange([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} draggable eventHandlers={{ dragend: (e) => { const m = e.target.getLatLng(); onChange([m.lat, m.lng]); } }} /> : null;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 11);
  }, [center, map]);
  return null;
}

const RiskGauge = ({ score, level }) => {
  const color = RISK_COLORS[level] || RISK_COLORS.Low;
  return (
    <div className="rl-gauge">
      <div className="rl-gauge-circle" style={{ borderColor: color, color }}>
        <span className="rl-gauge-score">{score}</span>
        <span className="rl-gauge-max">/100</span>
      </div>
      <div className="rl-gauge-level" style={{ background: color }}>{level} RISK</div>
    </div>
  );
};

const ReportLivestock = () => {
  const [mode, setMode] = useState('report'); // 'report' | 'check'

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [species, setSpecies] = useState('cattle');
  const [herdSize, setHerdSize] = useState('');
  const [affectedCount, setAffectedCount] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [notes, setNotes] = useState('');

  // Location
  const [position, setPosition] = useState(null); // [lat, lng]
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [locating, setLocating] = useState(false);
  const searchTimer = useRef(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // submit result OR risk-nearby result
  const [checking, setChecking] = useState(false);

  // Auto-detect location on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setPosition(p);
        setCenter(p);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not supported on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setPosition(p);
        setCenter(p);
        setLocating(false);
        toast.success('Location detected');
      },
      () => { setLocating(false); toast.error('Could not detect location — tap the map to set it'); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await satelliteApi.searchLocations(v);
        setSearchResults(r.success ? r.predictions || [] : []);
      } catch { setSearchResults([]); }
    }, 350);
  };

  const selectPlace = async (prediction) => {
    setSearchResults([]);
    setSearchQuery(prediction.description);
    try {
      const d = await satelliteApi.getPlaceDetails(prediction.placeId || prediction.place_id);
      if (d.success && d.location) {
        const p = [d.location.lat, d.location.lng];
        setPosition(p);
        setCenter(p);
      }
    } catch { toast.error('Could not load that place'); }
  };

  const toggleSymptom = (key) => {
    setSymptoms((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));
  };

  const canSubmit = name.trim() && phone.trim() && position && species;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!position) { toast.error('Please set your farm location on the map'); return; }
    if (!name.trim() || !phone.trim()) { toast.error('Please enter your name and phone'); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        lat: position[0],
        lng: position[1],
        species,
        herdSize: herdSize ? parseInt(herdSize, 10) : 0,
        affectedCount: affectedCount ? parseInt(affectedCount, 10) : 0,
        symptoms,
        notes: notes.trim(),
      };
      const res = await fmdApi.submitReport(payload);
      if (res.success) {
        setResult({ type: 'report', ...res });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error(res.message || 'Could not submit report');
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || 'Submission failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckRisk = async () => {
    if (!position) { toast.error('Set a location first (detect or tap the map)'); return; }
    setChecking(true);
    try {
      const res = await fmdApi.getRiskNearby(position[0], position[1]);
      if (res.success) {
        setResult({ type: 'check', ...res });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error(res.message || 'Could not compute risk');
      }
    } catch {
      toast.error('Could not compute risk');
    } finally {
      setChecking(false);
    }
  };

  const resetAll = () => {
    setResult(null);
    setSymptoms([]);
    setNotes('');
    setAffectedCount('');
  };

  // ---------- Success / result screen ----------
  if (result) {
    const risk = result.risk;
    return (
      <div className="rl-page">
        <div className="rl-card rl-result">
          {result.type === 'report' ? (
            <>
              <FiCheckCircle className="rl-result-icon ok" />
              <h2>Report received</h2>
              <p className="rl-case-id">Case ID: <strong>{result.caseId}</strong></p>
            </>
          ) : (
            <>
              <FiActivity className="rl-result-icon" />
              <h2>Outbreak risk near you</h2>
            </>
          )}

          <RiskGauge score={risk.score} level={risk.level} />

          <p className="rl-result-message">{result.message ||
            (risk.level === 'Severe' || risk.level === 'High'
              ? 'High transmission risk in your area. Isolate affected animals, avoid shared grazing and water, and limit animal movement.'
              : 'Risk is currently manageable. Keep monitoring your animals and avoid mixing with neighbouring herds.')}
          </p>

          <div className="rl-result-factors">
            <h4>What drives this score</h4>
            <ul>
              <li><span>Confirmed outbreaks near you (50&nbsp;km)</span><b>{risk.nearbyConfirmed ?? 0}</b></li>
              <li><span>Other herds that may share your grazing</span><b>{risk.grazingOverlap?.overlappingHerds ?? 0}</b></li>
              <li><span>How far your animals likely roam to graze</span><b>{risk.grazingOverlap?.catchmentRadiusKm ?? '—'} km</b></li>
              <li><span>Likely to share a water point</span><b>{risk.grazingOverlap?.sharedWater ? 'Yes ⚠️' : 'No'}</b></li>
              {risk.eoContext?.ndvi != null && <li><span>Grass / vegetation health (NDVI)</span><b>{risk.eoContext.ndvi}</b></li>}
              {risk.eoContext?.rainfallMm != null && <li><span>Recent rain in your area</span><b>{Math.round(risk.eoContext.rainfallMm)} mm</b></li>}
            </ul>
          </div>

          <div className="rl-how">
            <h4>How we worked this out</h4>
            <p>
              We checked <strong>live satellite & weather data</strong> for your exact location — not a guess.
              FMD spreads when animals <strong>mix</strong>, so we look at where your animals are likely to graze and
              drink, whether neighbouring herds share that area, how close any confirmed outbreaks are, and whether dry
              conditions are pushing animals onto the same water points.
            </p>
            <div className="rl-how-sources">
              <span>🛰️ Satellite vegetation (NDVI)</span>
              <span>🌧️ Rainfall & soil moisture</span>
              <span>🐄 Nearby herds</span>
              <span>🔴 Confirmed outbreaks</span>
            </div>
          </div>

          <div className="rl-result-actions">
            {result.type === 'report' ? (
              <button className="rl-btn-secondary" onClick={() => { setResult({ type: 'check', ...{ risk, location: { lat: position[0], lng: position[1] } } }); }}>
                View risk near me <FiArrowRight />
              </button>
            ) : null}
            <button className="rl-btn-primary" onClick={resetAll}>Submit another report</button>
          </div>
          <p className="rl-disclaimer">This is a preliminary, satellite-assisted estimate — not a veterinary diagnosis. For confirmed cases, contact your state veterinarian.</p>
        </div>
      </div>
    );
  }

  // ---------- Main form ----------
  return (
    <div className="rl-page">
      <header className="rl-header">
        <div className="rl-brand">
          <span className="rl-logo">🐄🛰️</span>
          <div>
            <h1>Livestock Health Report</h1>
            <p>Report sick animals & check Foot-and-Mouth outbreak risk in your area</p>
          </div>
        </div>
        <div className="rl-mode-toggle">
          <button className={mode === 'report' ? 'active' : ''} onClick={() => setMode('report')}>Report sick animals</button>
          <button className={mode === 'check' ? 'active' : ''} onClick={() => setMode('check')}>Check risk near me</button>
        </div>
      </header>

      {/* Location block — shared by both modes */}
      <div className="rl-card">
        <h3><FiMapPin /> Your farm location</h3>
        <div className="rl-location-controls">
          <button type="button" className="rl-detect" onClick={detectLocation} disabled={locating}>
            <FiCrosshair /> {locating ? 'Detecting…' : 'Use my location'}
          </button>
          <div className="rl-search">
            <FiSearch />
            <input type="text" placeholder="Or search a town / area…" value={searchQuery} onChange={onSearchChange} />
            {searchResults.length > 0 && (
              <ul className="rl-search-results">
                {searchResults.map((r, i) => (
                  <li key={r.placeId || i} onClick={() => selectPlace(r)}>{r.description}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="rl-map">
          <MapContainer center={center} zoom={position ? 11 : 5} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri" />
            <LocationPicker position={position} onChange={setPosition} />
            <Recenter center={center} />
          </MapContainer>
        </div>
        <p className="rl-map-hint">{position ? `📍 ${position[0].toFixed(4)}, ${position[1].toFixed(4)} — tap the map or drag the pin to adjust` : 'Tap the map to drop a pin on your farm'}</p>
      </div>

      {mode === 'check' ? (
        <div className="rl-card">
          <button className="rl-btn-primary rl-full" onClick={handleCheckRisk} disabled={checking || !position}>
            {checking ? 'Calculating…' : 'Check outbreak risk at this location'}
          </button>
        </div>
      ) : (
        <form className="rl-card" onSubmit={handleSubmit}>
          <h3>About your animals</h3>

          <label className="rl-field">
            <span>Your name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Dlamini" required />
          </label>
          <label className="rl-field">
            <span>Phone number</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 082 123 4567" required />
          </label>

          <span className="rl-field-label">Type of animal</span>
          <div className="rl-species">
            {SPECIES.map((s) => (
              <button type="button" key={s.key} className={`rl-species-btn ${species === s.key ? 'active' : ''}`} onClick={() => setSpecies(s.key)}>
                <span className="rl-species-emoji">{s.emoji}</span>
                <span>{s.label}</span>
              </button>
            ))}
          </div>

          <div className="rl-row">
            <label className="rl-field">
              <span>Total animals</span>
              <input type="number" min="0" value={herdSize} onChange={(e) => setHerdSize(e.target.value)} placeholder="e.g. 120" />
            </label>
            <label className="rl-field">
              <span>How many sick?</span>
              <input type="number" min="0" value={affectedCount} onChange={(e) => setAffectedCount(e.target.value)} placeholder="e.g. 8" />
            </label>
          </div>

          <span className="rl-field-label">What signs do you see? (tap all that apply)</span>
          <div className="rl-symptoms">
            {SYMPTOMS.map((sym) => (
              <button type="button" key={sym.key} className={`rl-symptom ${symptoms.includes(sym.key) ? 'active' : ''}`} onClick={() => toggleSymptom(sym.key)}>
                <span className="rl-symptom-emoji">{sym.emoji}</span>
                <span>{sym.label}</span>
              </button>
            ))}
          </div>

          <label className="rl-field">
            <span>Anything else? (optional)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Describe what you've noticed…" />
          </label>

          <button type="submit" className="rl-btn-primary rl-full" disabled={submitting || !canSubmit}>
            {submitting ? 'Sending…' : 'Submit report'}
          </button>
          {!canSubmit && <p className="rl-hint-warning"><FiAlertTriangle /> Add your name, phone and farm location to submit.</p>}
        </form>
      )}

      {/* Footer: emergency guidance + data-use note */}
      <footer className="rl-footer">
        <div className="rl-footer-alert">
          <FiAlertTriangle />
          <span>Suspected Foot-and-Mouth Disease is a <strong>notifiable</strong> condition. If you see blisters or
          severe drooling, also call your <strong>State Veterinarian</strong> immediately and restrict animal movement.</span>
        </div>
        <p className="rl-footer-note">
          Your details are used only to assess outbreak risk in your area and to contact you about your report.
          This tool gives a preliminary satellite-assisted estimate — it is not a veterinary diagnosis.
        </p>
        <p className="rl-footer-brand">🌍 Geoventory · FMD Biosecurity Intelligence</p>
      </footer>
    </div>
  );
};

export default ReportLivestock;
